import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import moment from 'moment';
import MetasForPage from '../../MetasForPage';
import { getTenantSlug } from '../../utils/subdomain';
import { Loading } from '../../components/Loading';
import { signInWithCustomToken, getIdToken } from '../../helpers/auth';
import { loginUser } from '../../ducks/Auth/actions';
import PublicChallengeView, { formatMoney } from './View';
// Bundled copy of the mobile app icon (same asset as AppDownloadCard).
import appIcon from '../../assets/logos/nubod-app-icon.png';
import './index.css';

// The PUBLIC challenge landing page (Phase 1). Served at
// <trainer-subdomain>.getnubod.com/challenge/<slug>, this is the trainer-built
// sales/launch page a stranger loads (shared on social). This wrapper owns the
// data fetch, the join modal, and the head/meta tags; the actual page render
// lives in the reusable, prop-driven <PublicChallengeView>, which the builder
// also renders (contained) for its live preview.
//
// Two ways in: a FREE challenge joins with an emailed 6-digit code (OTP); a
// PAID one (challenge.offer) collects name + email + plan and hands off to
// Stripe Checkout, which sends the buyer back here with ?checkout=success —
// the page then confirms the spot. Both end on a "get the app" step.
//
// Rendered as a fixed full-viewport layer so it's full-bleed (the app chrome
// hides behind it). All landingPage text is escaped React children — never HTML.

const API = process.env.REACT_APP_API_URL;
const APP_STORE_URL = 'https://apps.apple.com/us/app/nubod/id6759975198';
const APP_STORE_BADGE =
  'https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg';
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
// After Stripe sends the buyer back, the webhook may still be enrolling them —
// check back this many times before showing "we'll email you".
const CONFIRM_TRIES = 8;
const CONFIRM_EVERY_MS = 2500;

// What to do after joining: the app first (where the challenge happens), then
// how to sign in there — by emailed code, no password.
function NextSteps({ email, loginHref, signedInHere }) {
  return (
    <ol className='pcl-next'>
      <li>
        <div>
          <strong>Get the app.</strong> Your workouts, food log and check-ins live
          there.
          <div className='pcl-app'>
            <img className='pcl-app-icon' src={appIcon} alt='' />
            <a
              className='pcl-app-badge'
              href={APP_STORE_URL}
              target='_blank'
              rel='noopener noreferrer'
            >
              <img src={APP_STORE_BADGE} alt='Download on the App Store' />
            </a>
          </div>
        </div>
      </li>
      <li>
        <div>
          <strong>
            Sign in with {email ? email : 'the email you used'}.
          </strong>{' '}
          Choose “Sign in with a code” and we’ll email you a 6-digit code — no
          password needed.{' '}
          {signedInHere ? null : (
            <>
              No iPhone? <a href={loginHref}>Sign in on the web</a>.
            </>
          )}
        </div>
      </li>
      <li>
        <div>
          <strong>Answer a few quick questions</strong> so your targets are set
          before day one.
        </div>
      </li>
    </ol>
  );
}

export default function PublicChallengePage({ match, history }) {
  const slug = match && match.params && match.params.slug;
  const brandSlug = useMemo(() => getTenantSlug(), []);
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  // Join funnel state. Free: 'form' → 'code' → 'joined'. Paid: 'pay' → Stripe →
  // back here: 'confirming' → 'paid' (or 'pending' / 'attention').
  const [join, setJoin] = useState(null);
  const [jEmail, setJEmail] = useState('');
  const [jName, setJName] = useState('');
  const [jCode, setJCode] = useState('');
  const [jBusy, setJBusy] = useState(false);
  const [jError, setJError] = useState(null);
  const [jPlan, setJPlan] = useState('full'); // paid: 'full' | 'split'
  const [jNote, setJNote] = useState(null); // a quiet confirmation under the form
  const [paid, setPaid] = useState(null); // the confirm response once enrolled
  const alive = useRef(true);
  useEffect(() => () => { alive.current = false; }, []);

  useEffect(() => {
    let cancelled = false;
    const done = () => {
      // Let prerender.io capture once the async content has landed.
      if (typeof window !== 'undefined') window.prerenderReady = true;
    };
    (async () => {
      if (!brandSlug || !slug) {
        if (!cancelled) { setNotFound(true); setLoading(false); done(); }
        return;
      }
      try {
        const res = await fetch(
          `${API}/api/public/challenges/${encodeURIComponent(
            brandSlug
          )}/${encodeURIComponent(slug)}`
        );
        const json = await res.json();
        if (cancelled) return;
        if (!json || !json.found) setNotFound(true);
        else setData(json);
      } catch (_) {
        if (!cancelled) setNotFound(true);
      } finally {
        if (!cancelled) { setLoading(false); done(); }
      }
    })();
    return () => { cancelled = true; };
  }, [brandSlug, slug]);

  // The paid form's details, kept for this tab while the buyer is on Stripe so
  // a cancelled checkout comes back pre-filled. A convenience only.
  const draftKey = `pcl-checkout:${brandSlug}/${slug}`;
  const readDraft = () => {
    try {
      return JSON.parse(sessionStorage.getItem(draftKey) || 'null');
    } catch (_) {
      return null;
    }
  };
  const writeDraft = (d) => {
    try {
      if (d) sessionStorage.setItem(draftKey, JSON.stringify(d));
      else sessionStorage.removeItem(draftKey);
    } catch (_) {
      /* storage blocked — no pre-fill */
    }
  };

  // Back from Stripe Checkout (?checkout=success&session_id=… or
  // ?checkout=cancelled). Confirm the spot — the same safe-to-repeat enrollment
  // the webhook runs — checking back while the webhook is mid-way.
  const handledReturn = useRef(false);
  useEffect(() => {
    if (!data || handledReturn.current) return;
    let params;
    try {
      params = new URLSearchParams(window.location.search);
    } catch (_) {
      return;
    }
    const status = params.get('checkout');
    if (!status) return;
    handledReturn.current = true;
    const sessionId = params.get('session_id');
    // Drop the checkout params so a refresh or a shared link doesn't replay it
    // (the ?brand= override, if any, stays).
    const clearParams = () => {
      params.delete('checkout');
      params.delete('session_id');
      const qs = params.toString();
      const next = `${window.location.pathname}${qs ? `?${qs}` : ''}`;
      try {
        if (history && history.replace) history.replace(next);
        else window.history.replaceState(null, '', next);
      } catch (_) {
        /* cosmetic */
      }
    };
    if (status === 'cancelled') {
      clearParams();
      const d = readDraft();
      if (d) {
        if (d.name) setJName(d.name);
        if (d.email) setJEmail(d.email);
        if (d.plan) setJPlan(d.plan);
      }
      setJoin('cancelled');
      return;
    }
    if (status !== 'success' || !/^cs_(test|live)_[A-Za-z0-9]+$/.test(sessionId || '')) {
      clearParams();
      return;
    }
    const base = `${API}/api/public/challenges/${encodeURIComponent(
      brandSlug
    )}/${encodeURIComponent(slug)}`;
    setJoin('confirming');
    const attempt = async (n) => {
      if (!alive.current) return;
      let body = null;
      try {
        const res = await fetch(`${base}/checkout/confirm`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ sessionId }),
        });
        body = res.ok ? await res.json().catch(() => null) : null;
      } catch (_) {
        body = null;
      }
      if (!alive.current) return;
      if (body && body.status === 'enrolled') {
        setPaid(body);
        setJoin('paid');
        clearParams();
        writeDraft(null);
      } else if (body && body.status === 'needs_attention') {
        setJoin('attention');
        clearParams();
      } else if (n + 1 < CONFIRM_TRIES) {
        setTimeout(() => attempt(n + 1), CONFIRM_EVERY_MS);
      } else {
        // Stripe only sends buyers here after the payment went through; the
        // webhook finishes the spot and emails them.
        setJoin('pending');
        clearParams();
      }
    };
    attempt(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- runs once per load; the draft helpers only read brandSlug/slug
  }, [data, brandSlug, slug, history]);

  if (loading) {
    return (
      <div className='pcl-root pcl-center'>
        <Loading inline />
      </div>
    );
  }
  if (notFound || !data) {
    return (
      <div className='pcl-root pcl-center'>
        <div className='pcl-notfound'>
          <div className='pcl-eyebrow'>// not found</div>
          <h1 className='pcl-h1'>This challenge isn’t available.</h1>
          <p className='pcl-muted'>
            The link may be wrong, or the challenge is no longer public.
          </p>
        </div>
      </div>
    );
  }

  const { brand, challenge, cohort } = data;
  const lp = challenge.landingPage || {};
  const coach = (brand && brand.name) || null;
  const offer = challenge.offer || null;
  const spots = challenge.spots || null;
  const brandQuery = (() => {
    try {
      return new URLSearchParams(window.location.search).get('brand');
    } catch (_) {
      return null;
    }
  })();
  const loginHref = `/login${brandQuery ? `?brand=${encodeURIComponent(brandQuery)}` : ''}`;
  const startDate = (paid && paid.startDate) || (cohort && cohort.startDate) || null;
  const startTz = (paid && paid.timezone) || (cohort && cohort.timezone) || null;
  const startLabel = startDate
    ? (startTz && moment(startDate).tz ? moment(startDate).tz(startTz) : moment(startDate)).format(
        'dddd, MMM D'
      )
    : null;

  // Brand accent for the modal (which renders OUTSIDE .pcl-root, so it wouldn't
  // otherwise inherit the owner-brand --primary the view paints on its root).
  const brandColors = (brand && brand.colors) || null;
  const brandPrimary = Array.isArray(brandColors)
    ? brandColors[0]
    : brandColors && brandColors.primary;
  const modalStyle = brandPrimary
    ? { '--primary': brandPrimary, '--pcl-accent': brandPrimary }
    : undefined;

  const ogImg = `${API}/api/public/challenges/${encodeURIComponent(
    brandSlug
  )}/${encodeURIComponent(slug)}/image.png`;

  const joinBase = `${API}/api/public/challenges/${encodeURIComponent(
    brandSlug
  )}/${encodeURIComponent(slug)}`;

  const openJoin = () => {
    setJError(null);
    setJNote(null);
    setJCode('');
    setJoin(offer ? 'pay' : 'form');
  };
  const goToApp = () => {
    if (history && history.push) history.push('/dashboard/home');
    else window.location.assign('/dashboard/home');
  };
  const closeJoin = () => {
    if (jBusy || join === 'confirming') return;
    // A free joiner is signed in on the web by now — closing takes them in.
    if (join === 'joined') return goToApp();
    setJoin(null);
  };

  // Paid: hand off to Stripe Checkout. The API re-checks everything (round
  // open, spots left, not already in) before creating the session.
  const submitCheckout = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    const email = jEmail.trim();
    if (!EMAIL_RE.test(email)) {
      setJError('Enter a valid email address.');
      return;
    }
    setJError(null);
    setJBusy(true);
    try {
      const res = await fetch(`${joinBase}/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          name: jName.trim(),
          plan: offer && offer.splitPay ? jPlan : 'full',
          origin: window.location.origin,
        }),
      });
      const body = await res.json().catch(() => null);
      if (!res.ok || !body || !body.url) {
        setJError(
          (body && body.errors && body.errors[0] && body.errors[0].msg) ||
            'Couldn’t start checkout. Please try again.'
        );
        setJBusy(false);
        return;
      }
      writeDraft({ name: jName.trim(), email, plan: jPlan });
      // Stay "busy" while the browser leaves for Stripe.
      window.location.assign(body.url);
    } catch (_) {
      setJError('Couldn’t start checkout. Check your connection and try again.');
      setJBusy(false);
    }
  };

  // Add to the challenge waitlist. The WaitlistModal (in the View) owns the form
  // + confirmation UI; this just performs the POST and throws on failure so the
  // modal can show its error. Endpoint is enumeration-safe ({ok:true}).
  const submitWaitlist = async (email, firstName) => {
    const res = await fetch(`${joinBase}/waitlist`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, firstName }),
    });
    if (!res.ok) throw new Error('waitlist failed');
  };

  // Step 1 — request a code. The endpoint is ENUMERATION-SAFE ({ok:true}
  // regardless), so we always advance to the code step on a 200.
  const submitRequest = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    const email = jEmail.trim();
    if (!EMAIL_RE.test(email)) {
      setJError('Enter a valid email address.');
      return;
    }
    setJError(null);
    setJBusy(true);
    try {
      const res = await fetch(`${joinBase}/join/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, name: jName.trim() }),
      });
      if (!res.ok) throw new Error('request failed');
      setJoin('code');
    } catch (_) {
      setJError("Couldn't send the code. Check your connection and try again.");
    } finally {
      setJBusy(false);
    }
  };

  // Step 2 — verify the code, sign in with the returned custom token, and enter
  // the app authenticated (loginUser hydrates Redux; the callback routes them —
  // a fresh challenge client lands on the assessment / quick-setup).
  const submitVerify = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    const email = jEmail.trim();
    const code = jCode.trim();
    if (code.length !== 6) {
      setJError('Enter the 6-digit code from your email.');
      return;
    }
    setJError(null);
    setJBusy(true);
    try {
      const res = await fetch(`${joinBase}/join/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, code }),
      });
      const body = await res.json().catch(() => null);
      if (!res.ok || !body || !body.customToken) {
        setJError(
          (body && body.errors && body.errors[0] && body.errors[0].msg) ||
            'That code is invalid or has expired.'
        );
        setJBusy(false);
        return;
      }
      await signInWithCustomToken(body.customToken);
      const idToken = await getIdToken();
      localStorage.setItem('token', idToken);
      try {
        localStorage.setItem('viewMode', 'client');
      } catch (_) {
        /* private-mode browsers can reject localStorage writes */
      }
      // Hydrate Redux auth (POST /api/auth + loadUser). Then show the next
      // steps (get the app); "Continue on the web" navigates once auth is
      // hydrated, so PrivateRoute routes the new challenge client to their
      // assessment instead of bouncing to /login.
      dispatch(
        loginUser({ token: idToken }, (error) => {
          if (error) {
            setJError('Signed up, but sign-in failed. Use "Sign in with a code".');
            setJBusy(false);
            return;
          }
          setJBusy(false);
          setJoin('joined');
        })
      );
    } catch (_) {
      setJError('Something went wrong. Please try again.');
      setJBusy(false);
    }
  };

  const resendCode = async () => {
    setJError(null);
    setJNote(null);
    try {
      await fetch(`${joinBase}/join/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: jEmail.trim(), name: jName.trim() }),
      });
      setJNote('If that email is eligible, a new code is on its way.');
    } catch (_) {
      setJError("Couldn't resend the code. Check your connection and try again.");
    }
  };

  return (
    <>
      <MetasForPage
        loc='/challenge'
        config={{
          title: `${challenge.name}${coach ? ` · ${coach}` : ''}`,
          description:
            (lp.hero && lp.hero.subheadline) || challenge.description || '',
          img: ogImg,
        }}
      />

      <PublicChallengeView data={data} onJoin={openJoin} onWaitlist={submitWaitlist} />

      {/* Self-join modal — 2-step OTP. No account exists until the code is
          verified; entering someone else's email only mails THEM a code. */}
      {join ? (
        <div
          className='pcl-modal-overlay'
          data-theme={lp.theme || 'editorial'}
          style={modalStyle}
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) closeJoin();
          }}
        >
          <div
            className='pcl-modal'
            role='dialog'
            aria-modal='true'
            aria-label={`Join ${challenge.name}`}
          >
            <button
              type='button'
              className='pcl-modal-x'
              onClick={closeJoin}
              aria-label='Close'
            >
              ✕
            </button>

            {join === 'pay' && offer ? (
              <form onSubmit={submitCheckout}>
                <div className='pcl-eyebrow'>// join the challenge</div>
                <h3 className='pcl-modal-title'>{challenge.name}</h3>
                <p className='pcl-modal-sub'>
                  Enter your details, then pay on Stripe’s secure page. Use the
                  email you’ll sign in to the app with — no password needed.
                </p>
                <label className='pcl-modal-label'>Your name</label>
                <input
                  className='pcl-modal-input'
                  value={jName}
                  onChange={(e) => setJName(e.target.value)}
                  placeholder='Your name'
                  autoComplete='name'
                  autoFocus
                />
                <label className='pcl-modal-label'>Email</label>
                <input
                  className='pcl-modal-input'
                  value={jEmail}
                  onChange={(e) => setJEmail(e.target.value)}
                  placeholder='you@email.com'
                  type='email'
                  autoComplete='email'
                />
                {offer.splitPay ? (
                  <>
                    <div className='pcl-modal-label' id='pcl-plan-label'>
                      How would you like to pay?
                    </div>
                    <div className='pcl-plans' role='radiogroup' aria-labelledby='pcl-plan-label'>
                      <label className={`pcl-plan${jPlan === 'full' ? ' pcl-plan--on' : ''}`}>
                        <input
                          type='radio'
                          name='pcl-plan'
                          value='full'
                          checked={jPlan === 'full'}
                          onChange={() => setJPlan('full')}
                        />
                        <span className='pcl-plan-name'>Pay in full</span>
                        <span className='pcl-plan-amount'>
                          {formatMoney(offer.priceCents, offer.currency)}
                        </span>
                      </label>
                      <label className={`pcl-plan${jPlan === 'split' ? ' pcl-plan--on' : ''}`}>
                        <input
                          type='radio'
                          name='pcl-plan'
                          value='split'
                          checked={jPlan === 'split'}
                          onChange={() => setJPlan('split')}
                        />
                        <span className='pcl-plan-name'>
                          {offer.splitPay.count} monthly payments
                          <span className='pcl-plan-sub'>
                            {formatMoney(
                              offer.splitPay.count * offer.splitPay.amountCents,
                              offer.currency
                            )}{' '}
                            total · first payment today
                          </span>
                        </span>
                        <span className='pcl-plan-amount'>
                          {formatMoney(offer.splitPay.amountCents, offer.currency)}/mo
                        </span>
                      </label>
                    </div>
                  </>
                ) : null}
                {jError ? <div className='pcl-modal-error'>{jError}</div> : null}
                <button
                  type='submit'
                  className='pcl-cta pcl-modal-cta'
                  disabled={jBusy}
                >
                  {jBusy ? 'Opening checkout…' : 'Continue to payment'}{' '}
                  <span aria-hidden>→</span>
                </button>
                <div className='pcl-modal-foot'>
                  Secure checkout with Stripe
                  {spots ? ` · ${spots.left} of ${spots.total} spots left` : ''}
                </div>
              </form>
            ) : join === 'cancelled' ? (
              <div>
                <div className='pcl-eyebrow'>// checkout cancelled</div>
                <h3 className='pcl-modal-title'>No charge made.</h3>
                <p className='pcl-modal-sub'>
                  You left checkout before paying, so you weren’t charged — and
                  your spot isn’t held until you finish.
                </p>
                <button
                  type='button'
                  className='pcl-cta pcl-modal-cta'
                  onClick={() => {
                    setJError(null);
                    setJoin(offer ? 'pay' : 'form');
                  }}
                >
                  Try again <span aria-hidden>→</span>
                </button>
                <div className='pcl-modal-foot'>
                  <button type='button' className='pcl-modal-link' onClick={closeJoin}>
                    Not now
                  </button>
                </div>
              </div>
            ) : join === 'confirming' ? (
              <div role='status'>
                <div className='pcl-eyebrow'>// payment received</div>
                <h3 className='pcl-modal-title'>Confirming your spot…</h3>
                <div className='pcl-modal-spinner' aria-hidden='true' />
                <p className='pcl-modal-sub' style={{ textAlign: 'center' }}>
                  This takes a few seconds.
                </p>
              </div>
            ) : join === 'paid' ? (
              <div>
                <div className='pcl-eyebrow'>// you’re in</div>
                <h3 className='pcl-modal-title'>
                  {paid && paid.firstName ? `You’re in, ${paid.firstName}.` : 'You’re in.'}
                </h3>
                <p className='pcl-modal-sub'>
                  Your spot in <strong>{challenge.name}</strong> is confirmed
                  {startLabel ? <> — we start <strong>{startLabel}</strong></> : null}.
                  We’ve emailed you these steps; your receipt comes from Stripe.
                </p>
                <NextSteps loginHref={loginHref} />
                <button type='button' className='pcl-cta pcl-modal-cta' onClick={closeJoin}>
                  Done
                </button>
              </div>
            ) : join === 'pending' || join === 'attention' ? (
              <div>
                <div className='pcl-eyebrow'>// payment received</div>
                <h3 className='pcl-modal-title'>
                  {join === 'pending' ? 'Almost there.' : 'Your payment went through.'}
                </h3>
                <p className='pcl-modal-sub'>
                  {join === 'pending'
                    ? 'We’re finishing setting up your spot. You’ll get an email with your next steps in a few minutes.'
                    : `We couldn’t finish setting up your spot automatically, so ${
                        coach || 'your coach'
                      } has been notified and will email you.`}
                </p>
                <button type='button' className='pcl-cta pcl-modal-cta' onClick={closeJoin}>
                  Done
                </button>
              </div>
            ) : join === 'joined' ? (
              <div>
                <div className='pcl-eyebrow'>// you’re in</div>
                <h3 className='pcl-modal-title'>
                  {jName.trim() ? `You’re in, ${jName.trim().split(/\s+/)[0]}.` : 'You’re in.'}
                </h3>
                <p className='pcl-modal-sub'>
                  You’ve joined <strong>{challenge.name}</strong>
                  {startLabel ? <> — we start <strong>{startLabel}</strong></> : null}.
                  Here’s how to get ready:
                </p>
                <NextSteps email={jEmail.trim()} loginHref={loginHref} signedInHere />
                <button type='button' className='pcl-cta pcl-modal-cta' onClick={goToApp}>
                  Continue on the web <span aria-hidden>→</span>
                </button>
              </div>
            ) : join === 'form' ? (
              <form onSubmit={submitRequest}>
                <div className='pcl-eyebrow'>// join the challenge</div>
                <h3 className='pcl-modal-title'>{challenge.name}</h3>
                <p className='pcl-modal-sub'>
                  Enter your details — we’ll email you a 6-digit code to confirm
                  and get you in. No password needed.
                </p>
                <label className='pcl-modal-label'>Your name</label>
                <input
                  className='pcl-modal-input'
                  value={jName}
                  onChange={(e) => setJName(e.target.value)}
                  placeholder='Your name'
                  autoComplete='name'
                  autoFocus
                />
                <label className='pcl-modal-label'>Email</label>
                <input
                  className='pcl-modal-input'
                  value={jEmail}
                  onChange={(e) => setJEmail(e.target.value)}
                  placeholder='you@email.com'
                  type='email'
                  autoComplete='email'
                />
                {jError ? <div className='pcl-modal-error'>{jError}</div> : null}
                <button
                  type='submit'
                  className='pcl-cta pcl-modal-cta'
                  disabled={jBusy}
                >
                  {jBusy ? 'Sending…' : 'Email me a code'}{' '}
                  <span aria-hidden>→</span>
                </button>
              </form>
            ) : (
              <form onSubmit={submitVerify}>
                <div className='pcl-eyebrow'>// check your email</div>
                <h3 className='pcl-modal-title'>Enter your code</h3>
                <p className='pcl-modal-sub'>
                  We sent a 6-digit code to <strong>{jEmail}</strong>. Enter it
                  below to join.
                </p>
                <label className='pcl-modal-label'>6-digit code</label>
                <input
                  className='pcl-modal-input pcl-modal-code'
                  value={jCode}
                  onChange={(e) =>
                    setJCode(e.target.value.replace(/\D/g, '').slice(0, 6))
                  }
                  placeholder='••••••'
                  inputMode='numeric'
                  autoComplete='one-time-code'
                  autoFocus
                />
                {jError ? <div className='pcl-modal-error'>{jError}</div> : null}
                <button
                  type='submit'
                  className='pcl-cta pcl-modal-cta'
                  disabled={jBusy}
                >
                  {jBusy ? 'Joining…' : 'Join now'} <span aria-hidden>→</span>
                </button>
                <div className='pcl-modal-foot'>
                  {jNote || (
                    <>
                      Didn’t get it?{' '}
                      <button
                        type='button'
                        className='pcl-modal-link'
                        onClick={resendCode}
                        disabled={jBusy}
                      >
                        Resend
                      </button>
                    </>
                  )}
                </div>
              </form>
            )}
          </div>
        </div>
      ) : null}

    </>
  );
}
